import { useTranslation } from '@pancakeswap/localization'
import { ArrowBackIcon, ArrowForwardIcon, Flex, IconButton, Link, Skeleton, Text } from '@pancakeswap/uikit'
import { useQuery } from '@tanstack/react-query'
import { useActiveChainId } from 'hooks/useActiveChainId'
import { useEffect, useState } from 'react'
import { styled } from 'styled-components'
import { formatAmount } from 'utils/formatInfoNumbers'
import { infoClientWithChain } from 'utils/graphql'
import { getBlockExploreLink } from 'utils'
import { useAccount } from 'wagmi'
import { fetchWalletSwapHistory, SwapHistoryEntry } from './swapHistoryData'

const PAGE_SIZE = 10

const HistoryHeader = styled(Flex)<{ $hasRecentTransactions: boolean }>`
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  margin-top: ${({ $hasRecentTransactions }) => ($hasRecentTransactions ? '20px' : '0')};
  margin-bottom: 8px;

  ${({ theme }) => theme.mediaQueries.md} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`

const HistoryList = styled.div`
  margin-top: 8px;
  max-height: min(50vh, 420px);
  overflow-y: auto;
  overscroll-behavior: contain;
  padding-right: 4px;
`

const HistoryRow = styled(Flex)`
  justify-content: space-between;
  align-items: flex-start;
  flex-direction: column;
  gap: 4px;
  min-height: 64px;
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.cardBorder};

  ${({ theme }) => theme.mediaQueries.md} {
    align-items: center;
    flex-direction: row;
    gap: 12px;
    min-height: auto;
    padding: 10px 0;
  }
`

const SwapDetails = styled.div`
  min-width: 0;
  width: 100%;
`

const SwapLink = styled(Link)`
  line-height: 1.4;
  overflow-wrap: anywhere;
`

const UsdValue = styled(Text)`
  width: 100%;
  text-align: left;

  ${({ theme }) => theme.mediaQueries.md} {
    width: auto;
    text-align: right;
    white-space: nowrap;
  }
`

const PageButton = styled(IconButton)`
  width: 44px;
  min-width: 44px;
  min-height: 44px;
`

const Pagination = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 12px;
`

interface SwapHistoryProps {
  hasRecentTransactions?: boolean
}

const getSwapAmounts = (swap: SwapHistoryEntry) => {
  const token0In = Number(swap.amount0In)
  const token1In = Number(swap.amount1In)
  const inputAmount = token0In > 0 ? token0In : token1In
  const inputSymbol = token0In > 0 ? swap.pair.token0.symbol : swap.pair.token1.symbol
  const outputAmount = Number(swap.amount0Out) > 0 ? Number(swap.amount0Out) : Number(swap.amount1Out)
  const outputSymbol = Number(swap.amount0Out) > 0 ? swap.pair.token0.symbol : swap.pair.token1.symbol

  return `${formatAmount(inputAmount)} ${inputSymbol} → ${formatAmount(outputAmount)} ${outputSymbol}`
}

export default function SwapHistory({ hasRecentTransactions = false }: SwapHistoryProps) {
  const { t } = useTranslation()
  const { address: account } = useAccount()
  const { chainId } = useActiveChainId()
  const [page, setPage] = useState(0)
  const client = chainId ? infoClientWithChain(chainId) : undefined
  const {
    data: swaps = [],
    isFetching,
    isError,
  } = useQuery(
    ['wallet-swap-history', chainId, account, page],
    () => fetchWalletSwapHistory(client!, account!, PAGE_SIZE + 1, page * PAGE_SIZE),
    {
      enabled: Boolean(client && account && chainId),
      staleTime: 15_000,
      retry: 2,
    },
  )

  useEffect(() => {
    setPage(0)
  }, [account, chainId])

  const hasNextPage = swaps.length > PAGE_SIZE
  const visibleSwaps = swaps.slice(0, PAGE_SIZE)

  if (!account || !chainId) return null

  return (
    <div>
      <HistoryHeader $hasRecentTransactions={hasRecentTransactions}>
        <Text bold>{t('Swap History')}</Text>
        <Link external small href={getBlockExploreLink(account, 'address', chainId)}>
          {t('View all on explorer')}
        </Link>
      </HistoryHeader>

      {isFetching && swaps.length === 0 && <Skeleton height="72px" width="100%" />}
      {!isFetching && (!client || isError) && (
        <Text color="textSubtle" small>
          {t('Swap history is temporarily unavailable. You can view all transactions on the explorer.')}
        </Text>
      )}
      {!isFetching && client && !isError && swaps.length === 0 && (
        <Text color="textSubtle" small>
          {t('No swap history found for this wallet on the current network.')}
        </Text>
      )}
      {visibleSwaps.length > 0 && (
        <>
          <HistoryList>
            {visibleSwaps.map((swap) => (
              <HistoryRow key={swap.id}>
                <SwapDetails>
                  <SwapLink external href={getBlockExploreLink(swap.transaction.id, 'transaction', chainId)}>
                    {getSwapAmounts(swap)}
                  </SwapLink>
                  <Text color="textSubtle" fontSize="12px">
                    {new Date(Number(swap.timestamp) * 1000).toLocaleString()}
                  </Text>
                </SwapDetails>
                <UsdValue color="textSubtle" fontSize="12px">
                  ${formatAmount(Number(swap.amountUSD))}
                </UsdValue>
              </HistoryRow>
            ))}
          </HistoryList>
          {(page > 0 || hasNextPage) && (
            <Pagination>
              <PageButton
                variant="tertiary"
                scale="sm"
                disabled={page === 0 || isFetching}
                aria-label={t('Previous page')}
                title={t('Previous page')}
                onClick={() => setPage((value) => Math.max(0, value - 1))}
              >
                <ArrowBackIcon color={page === 0 ? 'textDisabled' : 'primary'} />
              </PageButton>
              <Text color="textSubtle" fontSize="12px">
                {t('Page %page%', { page: page + 1 })}
              </Text>
              <PageButton
                variant="tertiary"
                scale="sm"
                disabled={!hasNextPage || isFetching}
                aria-label={t('Next page')}
                title={t('Next page')}
                onClick={() => setPage((value) => value + 1)}
              >
                <ArrowForwardIcon color={!hasNextPage ? 'textDisabled' : 'primary'} />
              </PageButton>
            </Pagination>
          )}
        </>
      )}
    </div>
  )
}
